/* =====================================================================================
   Access: who can sign in, and what they can reach.

   Separate from the employment record because they are separate decisions. Someone can hold
   a login without being an employee (an advisor, a non-executive) and can be an employee
   without a login (a technician who never needs the commercial system). Conflating the two
   is how people end up with access nobody chose to give them.

   Adding a colleague requires owner or admin. Deliberately NOT hr: administering the workforce
   and granting access to the pipeline are different powers.

   NOTHING HERE SENDS AN EMAIL, and the word "invite" was removed because it said otherwise.
   This writes a member record and grants roles; it does not create a login and does not notify
   anybody. Somebody read "Send invitation", reasonably assumed a message had gone out, created
   the Supabase login by hand as well, and then could not work out how to grant a role without
   triggering a second email that was never going to be sent. The screen caused that, so the
   screen says plainly what it does and what still has to happen.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import {
  listMembers, inviteMember, createLoginAndEmailLink, grantRole, revokeRole,
  revokeInvitation,
  ROLES, type Member,
} from '../lib/crew';
import { MfaSettings } from './StepUp';
import { Loading } from './Loading';
import './access.css';

export function AccessPanel({ canGrantRoles = true }: { canGrantRoles?: boolean }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ email: '', fullName: '', jobTitle: '',
                               roles: ['commercial'] as string[], createLogin: true });
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    setLoading(true);
    listMembers().then(setMembers).catch((e) => setErr(String(e.message ?? e)))
      .finally(() => setLoading(false));
  }, [nonce]);

  const toggle = (r: string) =>
    setF((p) => ({ ...p, roles: p.roles.includes(r) ? p.roles.filter((x) => x !== r) : [...p.roles, r] }));

  const [added, setAdded] = useState<string | null>(null);

  async function add() {
    setErr(null); setAdded(null);
    const who = f.fullName.trim() || f.email.trim();
    try {
      /* Access first, login second, and the order matters. The member record and roles are what
         actually grant anything; the login is only a way in. If the second step fails they still
         have access and can be sent a link later, whereas the reverse would leave somebody able
         to sign in to nothing and no record of why. */
      await inviteMember({ email: f.email, fullName: f.fullName, jobTitle: f.jobTitle, roles: f.roles });

      let note = `${who} now has access.`;
      if (f.createLogin) {
        try {
          await createLoginAndEmailLink(f.email);
          note += ' Their Supabase login has been created and a sign-in link emailed to them.';
        } catch (e) {
          // Access is already granted, so this is a partial success, not a failure.
          note += ' Access is granted, but the sign-in link could not be sent: '
                + (e instanceof Error ? e.message : String(e))
                + ' They can still sign in at this address themselves.';
        }
      } else {
        note += ' No email has been sent and no login was created. They sign in at this address '
              + 'and their account links itself the first time.';
      }
      setAdded(note);
      setF({ email: '', fullName: '', jobTitle: '', roles: ['commercial'], createLogin: true });
      setAdding(false); reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <>
      <section className="site-block">
        <header className="ac-head">
          <div>
            <h2>Access</h2>
            <p className="block-lead">
              Who can sign in and what they can reach. An account with no roles can sign in and
              see nothing, which is the safe default rather than a fault.
            </p>
          </div>
          <button className="btn-compose q-new" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : 'Add a colleague'}
          </button>
        </header>

        {err && <p className="block-error" role="alert">{err}</p>}

        {/* Says what happened AND what has not happened, because the absence of an email is the
            thing people get wrong here. */}
        {added && <p className="ac-added" role="status">{added}</p>}

        {adding && (
          <div className="composer card">
            <div className="composer-row">
              <input placeholder="Email address" value={f.email} className="grow"
                     onChange={(e) => setF({ ...f, email: e.target.value })} />
              <input placeholder="Full name" value={f.fullName} className="grow"
                     onChange={(e) => setF({ ...f, fullName: e.target.value })} />
              <input placeholder="Job title" value={f.jobTitle}
                     onChange={(e) => setF({ ...f, jobTitle: e.target.value })} />
            </div>

            <div className="ac-roles">
              {ROLES.map((r) => (
                <label key={r.key} className={`ac-role ${f.roles.includes(r.key) ? 'on' : ''}`}>
                  <input type="checkbox" checked={f.roles.includes(r.key)}
                         onChange={() => toggle(r.key)} />
                  <span className="ac-role-label">{r.label}</span>
                  <span className="ac-role-note">{r.note}</span>
                </label>
              ))}
            </div>

            <label className="ac-optin">
              <input type="checkbox" checked={f.createLogin}
                     onChange={(e) => setF({ ...f, createLogin: e.target.checked })} />
              <span>
                <strong>Create their login and email them a sign-in link.</strong>
                {' '}Leave this off if you have already made their account in Supabase, or if you
                would rather tell them yourself. Either way they can always sign in at this
                address and their account links itself the first time.
              </span>
            </label>
            <p className="cf-note">
              Only the owner may grant Owner or Admin. Leadership sees the whole diary including
              patent deadlines, so pick it deliberately. Someone with no roles can sign in and
              see nothing, which is the safe default rather than a fault.
            </p>

            <div className="composer-actions">
              <button className="btn-save" disabled={!f.email.trim() || !f.fullName.trim()}
                      onClick={add}>Add them</button>
            </div>
          </div>
        )}

        {loading ? <Loading label="Loading people" /> : (
          <table className="data">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Status</th><th>Roles</th><th /></tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td><strong>{m.full_name ?? '—'}</strong>
                    {m.job_title && <div className="muted">{m.job_title}</div>}</td>
                  <td className="mono">{m.email}</td>
                  <td>
                    {/* Someone who has never signed in looks different from a live account,
                        because the follow-up is different: one needs chasing, the other does
                        not. It says "has not signed in" rather than "invited", since nobody
                        was ever sent anything to accept. */}
                    {m.auth_user_id
                      ? <span className={`pill ${m.status === 'active' ? 'ok' : 'mute'}`}>{m.status}</span>
                      : <span className="pill warn" title="No email was sent. They sign in at this address and the account links itself.">
                          has not signed in
                        </span>}
                  </td>
                  <td>
                    <div className="ac-chips">
                      {/* Roles are owner-only in the database. An administrator can invite and
                          edit members and cannot change what anyone may reach, so for them the
                          chips are shown as state rather than as buttons. */}
                      {ROLES.map((r) => {
                        const has = m.roles.includes(r.key);
                        if (!canGrantRoles) {
                          return has
                            ? <span key={r.key} className="ac-chip on static" title={r.note}>
                                {r.label}
                              </span>
                            : null;
                        }
                        return (
                          <button key={r.key} title={r.note}
                                  className={`ac-chip ${has ? 'on' : ''}`}
                                  onClick={() => (has ? revokeRole(m.id, r.key) : grantRole(m.id, r.key))
                                    .then(reload).catch((e) => setErr(String(e.message ?? e)))}>
                            {r.label}
                          </button>
                        );
                      })}
                      {!canGrantRoles && m.roles.length === 0 && (
                        <span className="muted">no roles</span>
                      )}
                    </div>
                  </td>
                  <td className="num">
                    <button className="link-btn"
                            onClick={() => revokeInvitation(m.id).then((r) => { setErr(null); alert(r); reload(); })
                              .catch((e) => setErr(String(e.message ?? e)))}>
                      {m.auth_user_id ? 'Suspend' : 'Withdraw'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <MfaSettings />
    </>
  );
}
